import { useEffect, useRef, useState } from 'react';
import { LocationLookup } from './LocationLookup';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ValuationResult {
  priceRange: {
    min: number;
    max: number;
    currency: string;
  };
  justification: string;
  marketData: {
    recent: {
      listings: Array<{
        price: number;
        date: string;
        location: string;
        condition: string;
        currency: string;
        url?: string;
      }>;
      explanation?: string;
    };
    current: {
      listings: Array<{
        price: number;
        location: string;
        condition: string;
        currency: string;
        url?: string;
      }>;
      explanation?: string;
    };
  };
  rawContent: string;
  citations: string[];
  searchResults: Array<{
    title: string;
    url: string;
    date: string | null;
  }>;
}

interface AlbumResult {
  artist: string;
  album: string;
  year: string;
  genre: string;
  country: string;
  tracks: string[];
  rawContent: string;
  citations: string[];
  searchResults: Array<{
    title: string;
    url: string;
    date: string | null;
  }>;
}

interface Batch {
  _id: string;
  num: number;
  itemType: 'vinyl' | 'poster';
  createdAt: string;
  closedAt?: string | null;
  itemSeq: number;
}

interface Item {
  _id: string;
  batch: string;
  itemType: 'vinyl' | 'poster';
  sku: string;
  valuations: unknown[];
  images: string[];
  createdAt: string;
  itemNum: number;
}

interface AlbumProps {
  credentials: { username: string; password: string };
  dbMode: 'demo' | 'live';
  onAuthError?: () => void;
}

function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
    navigator.userAgent,
  );
}

export function Album({ credentials, dbMode, onAuthError }: AlbumProps) {
  // Album tab state
  const [albumResult, setAlbumResult] = useState<AlbumResult | null>(null);
  const [albumError, setAlbumError] = useState('');
  const [albumLoading, setAlbumLoading] = useState(false);
  const albumInputRef = useRef<HTMLInputElement>(null);
  const [albumImagePreview, setAlbumImagePreview] = useState<string | null>(
    null,
  );
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [isEnumeratingDevices, setIsEnumeratingDevices] = useState(false);

  // Inventory state for Album view
  const [addToInventory, setAddToInventory] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<Batch | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [savedItem, setSavedItem] = useState<Item | null>(null);
  const [savingItem, setSavingItem] = useState(false);
  const [saveError, setSaveError] = useState('');

  // New album valuation state
  const [albumValuationLocation, setAlbumValuationLocation] =
    useState('Worldwide');
  const [albumValuationError, setAlbumValuationError] = useState('');
  const [albumValuationLoading, setAlbumValuationLoading] = useState(false);
  const [albumValuationResult, setAlbumValuationResult] =
    useState<ValuationResult | null>(null);
  const albumValuationInputRef = useRef<HTMLInputElement>(null);
  const [albumValuationCapturedBlob, setAlbumValuationCapturedBlob] =
    useState<Blob | null>(null);
  const [albumValuationImagePreview, setAlbumValuationImagePreview] = useState<
    string | null
  >(null);
  const [showValuationCamera, setShowValuationCamera] = useState(false);
  const valuationVideoRef = useRef<HTMLVideoElement>(null);

  // Function to get available video devices
  const getVideoDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(
        (device) => device.kind === 'videoinput',
      );
      setVideoDevices(videoInputs);
      if (videoInputs.length > 0 && !selectedCamera) {
        setSelectedCamera(videoInputs[0].deviceId);
      }
    } catch (error) {
      console.error('Error getting video devices:', error);
    } finally {
      setIsEnumeratingDevices(false);
    }
  };

  // When showCamera is set to true, trigger getUserMedia to prompt for permissions and enumerate devices
  useEffect(() => {
    if (showCamera) {
      (async () => {
        try {
          await navigator.mediaDevices.getUserMedia({ video: true });
        } catch (err) {
          console.warn('Camera permission denied or error:', err);
        }
        getVideoDevices();
      })();
    }
  }, [showCamera]);

  // Attach stream to video element when stream or showCamera changes
  useEffect(() => {
    if (showCamera && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play();
      };
    }
  }, [cameraStream, showCamera]);

  // Update video stream when camera selection changes
  const startVideoStream = async (deviceId: string) => {
    try {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: deviceId ? { exact: deviceId } : undefined },
        });
      } catch (err) {
        console.warn(
          'Failed to get selected camera, falling back to default',
          err,
        );
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setShowCamera(false);
    }
  };

  // Camera modal logic
  useEffect(() => {
    if (showCamera && selectedCamera) {
      startVideoStream(selectedCamera);
    } else {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
      setCameraStream(null);
    }
  }, [showCamera, selectedCamera]);

  // Handle camera selection change
  const handleCameraChange = (deviceId: string) => {
    setSelectedCamera(deviceId);
  };

  // Listen for device changes when camera modal is open
  useEffect(() => {
    if (!showCamera) return;
    const handleDeviceChange = () => {
      getVideoDevices();
    };
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
    };
  }, [showCamera, getVideoDevices]);

  // Album image preview
  const handleAlbumImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (albumImagePreview) {
        URL.revokeObjectURL(albumImagePreview);
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAlbumImagePreview(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
      setCapturedBlob(null);
    } else {
      if (albumImagePreview) {
        URL.revokeObjectURL(albumImagePreview);
      }
      setAlbumImagePreview(null);
    }
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          setCapturedBlob(blob);
          if (albumImagePreview) {
            URL.revokeObjectURL(albumImagePreview);
          }
          setAlbumImagePreview(URL.createObjectURL(blob));
          setShowCamera(false);
          if (albumInputRef.current) {
            albumInputRef.current.value = '';
          }
        }
      }, 'image/jpeg');
    }
  };

  // Album form submit
  const handleAlbumSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAlbumError('');
    setAlbumLoading(true);
    setAlbumResult(null);

    let file: File | null = albumInputRef.current?.files?.[0] || null;
    if (!file && capturedBlob) {
      file = new File([capturedBlob], 'captured.jpg', { type: 'image/jpeg' });
    }
    if (!file) {
      setAlbumError('Please select or capture an album cover image.');
      setAlbumLoading(false);
      return;
    }

    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const base64 = reader.result as string;
          const base64Data = base64.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = () => reject(reader.error);
      });
      reader.readAsDataURL(file);

      const base64Data = await base64Promise;
      const response = await fetch('/api/recognize-album', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
        },
        body: JSON.stringify({ imageData: base64Data, mimeType: file.type }),
      });

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      if (!response.ok) {
        setAlbumError('Could not identify the album cover.');
        setAlbumLoading(false);
        return;
      }

      const data = await response.json();
      if (!data.artist || !data.album) {
        setAlbumError('Could not identify the album cover.');
      } else {
        setAlbumResult(data);
      }
    } catch {
      setAlbumError('An error occurred. Please try again.');
    } finally {
      setAlbumLoading(false);
    }
  };

  // Album valuation image change
  const handleAlbumValuationImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (albumValuationImagePreview) {
        URL.revokeObjectURL(albumValuationImagePreview);
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAlbumValuationImagePreview(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
      setAlbumValuationCapturedBlob(null);
    } else {
      if (albumValuationImagePreview) {
        URL.revokeObjectURL(albumValuationImagePreview);
      }
      setAlbumValuationImagePreview(null);
    }
  };

  const handleValuationCapturePhoto = () => {
    if (!valuationVideoRef.current) return;
    const video = valuationVideoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          setAlbumValuationCapturedBlob(blob);
          if (albumValuationImagePreview) {
            URL.revokeObjectURL(albumValuationImagePreview);
          }
          setAlbumValuationImagePreview(URL.createObjectURL(blob));
          setShowValuationCamera(false);
          if (albumValuationInputRef.current) {
            albumValuationInputRef.current.value = '';
          }
        }
      }, 'image/jpeg');
    }
  };

  // Inventory functions for Album view
  const fetchCurrentBatch = async () => {
    setBatchLoading(true);
    setBatchError('');
    try {
      const response = await fetch(`/api/batch/current/vinyl?db=${dbMode}`, {
        headers: {
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
        },
      });

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        const batch = await response.json();
        setCurrentBatch(batch);
      } else {
        setCurrentBatch(null);
      }
    } catch (error) {
      setBatchError('Failed to fetch current batch');
      console.error('Error fetching current batch:', error);
    } finally {
      setBatchLoading(false);
    }
  };

  const createNewBatch = async () => {
    setBatchLoading(true);
    setBatchError('');
    try {
      if (currentBatch) {
        await fetch(`/api/batch/${currentBatch.num}?db=${dbMode}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
          },
        });
      }

      const response = await fetch(`/api/batch?db=${dbMode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
        },
        body: JSON.stringify({ itemType: 'vinyl' }),
      });

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        const newBatch = await response.json();
        if (
          newBatch &&
          typeof newBatch.num === 'number' &&
          !isNaN(newBatch.num)
        ) {
          setCurrentBatch(newBatch);
        } else {
          setBatchError('Batch creation failed: invalid response from server.');
          setCurrentBatch(null);
        }
      } else {
        const errorData = await response.json();
        setBatchError(errorData.error || 'Failed to create new batch');
        setCurrentBatch(null);
      }
    } catch (error) {
      setBatchError('Failed to create new batch');
      setCurrentBatch(null);
      console.error('Error creating new batch:', error);
    } finally {
      setBatchLoading(false);
    }
  };

  const closeCurrentBatch = async () => {
    if (!currentBatch) return;
    setBatchLoading(true);
    setBatchError('');
    try {
      const response = await fetch(
        `/api/batch/${currentBatch.num}?db=${dbMode}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
          },
        },
      );

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        setCurrentBatch(null);
      } else {
        const errorData = await response.json();
        setBatchError(errorData.error || 'Failed to close batch');
      }
    } catch (error) {
      setBatchError('Failed to close batch');
      console.error('Error closing batch:', error);
    } finally {
      setBatchLoading(false);
    }
  };

  const saveItemToInventory = async () => {
    if (!currentBatch || !albumResult) return;
    setSavingItem(true);
    setSaveError('');
    try {
      const images: string[] = [];
      if (albumImagePreview) {
        images.push(albumImagePreview);
      }
      const valuations = albumValuationResult ? [albumValuationResult] : [];
      const response = await fetch(`/api/items?db=${dbMode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
        },
        body: JSON.stringify({
          batchId: currentBatch._id,
          itemType: 'vinyl',
          valuations,
          images,
        }),
      });

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        const item = await response.json();
        setSavedItem(item);
      } else {
        const errorData = await response.json();
        setSaveError(errorData.error || 'Failed to save item');
      }
    } catch (error) {
      setSaveError('Failed to save item');
      console.error('Error saving item:', error);
    } finally {
      setSavingItem(false);
    }
  };

  const resetForm = () => {
    setAlbumResult(null);
    setAlbumValuationResult(null);
    setAlbumImagePreview(null);
    setAlbumValuationImagePreview(null);
    setCapturedBlob(null);
    setAlbumValuationCapturedBlob(null);
    setSavedItem(null);
    setSaveError('');
    setAlbumError('');
    setAlbumValuationError('');
    if (albumInputRef.current) {
      albumInputRef.current.value = '';
    }
    if (albumValuationInputRef.current) {
      albumValuationInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (addToInventory) {
      fetchCurrentBatch();
    } else {
      setCurrentBatch(null);
      setSavedItem(null);
    }
  }, [addToInventory]);

  useEffect(() => {
    if (addToInventory) {
      fetchCurrentBatch();
    }
  }, [dbMode]);

  return (
    <>
      {/* Inventory Section - always visible in Album tab */}
      <Card className='mb-10 mt-6 w-full'>
        <CardHeader>
          <CardTitle>Inventory Management</CardTitle>
          <CardDescription>
            Add this album to your inventory for tracking and valuation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='grid w-full items-center gap-4'>
            {/* Add to Inventory Checkbox */}
            <div className='flex items-center space-x-2'>
              <input
                type='checkbox'
                id='add-to-inventory'
                checked={addToInventory}
                onChange={(e) => setAddToInventory(e.target.checked)}
                className='h-4 w-4 rounded border-gray-300'
              />
              <Label htmlFor='add-to-inventory'>Add to Inventory</Label>
            </div>

            {/* Inventory Fields - Only show when checkbox is checked */}
            {addToInventory && (
              <>
                {/* SKU Field */}
                <div className='flex flex-col space-y-1.5'>
                  <Label htmlFor='sku'>SKU</Label>
                  <Input
                    id='sku'
                    value={albumResult && savedItem?.sku ? savedItem.sku : ''}
                    readOnly
                    placeholder='Will be generated when saved'
                  />
                </div>

                {/* Batch Field */}
                <div className='flex flex-col space-y-1.5'>
                  <Label htmlFor='batch'>Batch</Label>
                  <div className='flex space-x-2'>
                    <Input
                      id='batch'
                      value={
                        currentBatch &&
                        typeof currentBatch.num === 'number' &&
                        !isNaN(currentBatch.num)
                          ? `Batch ${currentBatch.num}`
                          : 'None'
                      }
                      readOnly
                      className='flex-1'
                    />
                    <Button
                      type='button'
                      variant='outline'
                      onClick={createNewBatch}
                      disabled={batchLoading}
                    >
                      {batchLoading ? 'Creating...' : 'New'}
                    </Button>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={closeCurrentBatch}
                      disabled={!currentBatch || batchLoading}
                    >
                      {batchLoading ? 'Closing...' : 'Close'}
                    </Button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className='flex space-x-2'>
                  <Button
                    type='button'
                    onClick={saveItemToInventory}
                    disabled={
                      !currentBatch ||
                      !!currentBatch.closedAt ||
                      savingItem ||
                      !albumResult
                    }
                    className='flex-1'
                  >
                    {savingItem ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={resetForm}
                    className='flex-1'
                  >
                    Next Item
                  </Button>
                </div>

                {/* Error Messages */}
                {batchError && (
                  <p className='text-sm text-destructive'>{batchError}</p>
                )}
                {saveError && (
                  <p className='text-sm text-destructive'>{saveError}</p>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <h2 className='mb-2 mt-2 text-xl font-bold'>Album Recognition</h2>
      <Card className='w-full'>
        <CardHeader>
          <CardTitle>Album Cover Recognition</CardTitle>
          <CardDescription>
            Upload or capture an album cover image to identify the album.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleAlbumSubmit}>
          <CardContent>
            <div className='grid w-full items-center gap-4'>
              <div className='flex flex-col space-y-1.5'>
                <Label htmlFor='album-image'>Album Cover Image</Label>
                <Input
                  id='album-image'
                  type='file'
                  accept='image/*'
                  ref={albumInputRef}
                  onChange={handleAlbumImageChange}
                />
                {!isMobileDevice() && (
                  <Button
                    type='button'
                    variant='outline'
                    className='mt-2'
                    onClick={() => setShowCamera((v) => !v)}
                  >
                    {showCamera ? 'Close Camera' : 'Take Photo'}
                  </Button>
                )}
                {showCamera && (
                  <div className='mt-4 flex flex-col items-center'>
                    <div className='mb-2 w-full max-w-xs'>
                      <Select
                        value={selectedCamera}
                        onValueChange={handleCameraChange}
                        disabled={
                          isEnumeratingDevices || videoDevices.length === 0
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isEnumeratingDevices
                                ? 'Searching for available cameras...'
                                : videoDevices.length === 0
                                  ? 'No camera devices found'
                                  : 'Select camera'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {isEnumeratingDevices ? (
                            <SelectItem value='__searching__' disabled>
                              Searching for available cameras...
                            </SelectItem>
                          ) : videoDevices.length === 0 ? (
                            <SelectItem value='__none__' disabled>
                              No camera devices found
                            </SelectItem>
                          ) : (
                            videoDevices.map((device) => (
                              <SelectItem
                                key={device.deviceId}
                                value={device.deviceId}
                              >
                                {device.label ||
                                  `Camera ${videoDevices.indexOf(device) + 1}`}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      width={320}
                      height={240}
                      className='mb-2 aspect-video w-full max-w-xs rounded bg-black object-contain'
                      style={{ maxHeight: 320 }}
                    />
                    <Button
                      type='button'
                      onClick={handleCapturePhoto}
                      className='mb-2'
                      disabled={
                        isEnumeratingDevices || videoDevices.length === 0
                      }
                    >
                      Capture
                    </Button>
                  </div>
                )}
                {albumImagePreview && !showCamera ? (
                  <img
                    src={albumImagePreview}
                    alt='Album preview'
                    className='mt-2 max-h-48 w-full rounded border object-contain shadow'
                    style={{ background: '#f3f3f3' }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display =
                        'none';
                    }}
                  />
                ) : (
                  !showCamera && (
                    <span className='mt-2 text-sm text-muted-foreground'>
                      No preview available
                    </span>
                  )
                )}
              </div>
              {albumError && (
                <p className='text-sm text-destructive'>{albumError}</p>
              )}
            </div>
          </CardContent>
          <CardFooter>
            <Button className='w-full' type='submit' disabled={albumLoading}>
              {albumLoading ? 'Identifying...' : 'Identify Album'}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {albumResult && (
        <Card className='mt-6 w-full'>
          <CardHeader>
            <CardTitle>Album Recognition Result</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='mb-6'>
              <h3 className='mb-2 text-lg font-semibold'>Album</h3>
              <p className='text-2xl font-bold'>{albumResult.album}</p>
              <p className='mb-2 text-lg text-muted-foreground'>
                {albumResult.artist}
              </p>
              <p className='mb-2 text-sm text-muted-foreground'>
                {albumResult.year} • {albumResult.genre} • {albumResult.country}
              </p>
            </div>
            <div className='mb-6'>
              <h3 className='mb-2 text-lg font-semibold'>Track List</h3>
              {albumResult.tracks && albumResult.tracks.length > 0 ? (
                <ol className='list-decimal space-y-1 pl-6'>
                  {albumResult.tracks.map((track, idx) => (
                    <li key={idx}>{track}</li>
                  ))}
                </ol>
              ) : (
                <span className='text-muted-foreground'>unavailable</span>
              )}
            </div>
            {albumResult.citations && albumResult.citations.length > 0 && (
              <div className='mt-6'>
                <h3 className='mb-2 text-lg font-semibold'>Citations</h3>
                <ul className='list-disc space-y-1 pl-4'>
                  {albumResult.citations.map((url, index) => (
                    <li key={index}>
                      <a
                        href={url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-blue-500 hover:underline'
                      >
                        {url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {albumResult.searchResults &&
              albumResult.searchResults.length > 0 && (
                <div className='mt-6'>
                  <h3 className='mb-2 text-lg font-semibold'>Search Results</h3>
                  <ul className='space-y-2'>
                    {albumResult.searchResults.map((result, index) => (
                      <li key={index} className='border-b pb-2'>
                        <a
                          href={result.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-blue-500 hover:underline'
                        >
                          {result.title}
                        </a>
                        {result.date && (
                          <span className='ml-2 text-sm text-muted-foreground'>
                            ({result.date})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </CardContent>
        </Card>
      )}

      <div className='mt-10'>
        <h2 className='mb-2 text-xl font-bold'>Album Valuation</h2>
        <p className='mb-4 text-muted-foreground'>
          Get a fair market valuation for your vinyl record pressing. Please
          provide a clear image of the album pressing (not the cover) and select
          your location.
        </p>
        <Card className='w-full'>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setAlbumValuationError('');
              setAlbumValuationLoading(true);
              setAlbumValuationResult(null);
              let file: File | null =
                albumValuationInputRef.current?.files?.[0] || null;
              if (!file && albumValuationCapturedBlob) {
                file = new File(
                  [albumValuationCapturedBlob],
                  'valuation-captured.jpg',
                  {
                    type: 'image/jpeg',
                  },
                );
              }
              if (!file) {
                setAlbumValuationError(
                  'Please select or capture an image of the album pressing.',
                );
                setAlbumValuationLoading(false);
                return;
              }
              const reader = new FileReader();
              const base64Promise = new Promise<string>((resolve, reject) => {
                reader.onload = () => {
                  const base64 = reader.result as string;
                  const base64Data = base64.split(',')[1];
                  resolve(base64Data);
                };
                reader.onerror = () => reject(reader.error);
              });
              reader.readAsDataURL(file);
              const base64Data = await base64Promise;
              const album = albumResult?.album || '';
              const artist = albumResult?.artist || '';
              if (!albumValuationLocation.trim()) {
                setAlbumValuationError('Please select location.');
                setAlbumValuationLoading(false);
                return;
              }
              try {
                const response = await fetch('/api/album-valuation', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
                  },
                  body: JSON.stringify({
                    imageData: base64Data,
                    mimeType: file.type,
                    location: albumValuationLocation,
                    album,
                    artist,
                  }),
                });
                if (response.status === 401) {
                  if (onAuthError) onAuthError();
                  return;
                }
                if (!response.ok) {
                  const errorData = await response.json();
                  setAlbumValuationError(
                    errorData.error || 'Failed to get album valuation',
                  );
                  setAlbumValuationLoading(false);
                  return;
                }
                const data = await response.json();
                setAlbumValuationResult(data);
              } catch {
                setAlbumValuationError('An error occurred. Please try again.');
              } finally {
                setAlbumValuationLoading(false);
              }
            }}
          >
            <CardContent>
              <div className='grid w-full items-center gap-4'>
                <div className='mt-4 flex flex-col space-y-1.5'>
                  <Input
                    id='album-valuation-image'
                    type='file'
                    accept='image/*'
                    ref={albumValuationInputRef}
                    onChange={handleAlbumValuationImageChange}
                  />
                  {!isMobileDevice() && (
                    <Button
                      type='button'
                      variant='outline'
                      className='mt-2'
                      onClick={() => setShowValuationCamera((v) => !v)}
                    >
                      {showValuationCamera ? 'Close Camera' : 'Take Photo'}
                    </Button>
                  )}
                  {showValuationCamera && (
                    <div className='mt-4 flex flex-col items-center'>
                      <div className='mb-2 w-full max-w-xs'>
                        <Select
                          value={selectedCamera}
                          onValueChange={handleCameraChange}
                          disabled={
                            isEnumeratingDevices || videoDevices.length === 0
                          }
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                isEnumeratingDevices
                                  ? 'Searching for available cameras...'
                                  : videoDevices.length === 0
                                    ? 'No camera devices found'
                                    : 'Select camera'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {isEnumeratingDevices ? (
                              <SelectItem value='__searching__' disabled>
                                Searching for available cameras...
                              </SelectItem>
                            ) : videoDevices.length === 0 ? (
                              <SelectItem value='__none__' disabled>
                                No camera devices found
                              </SelectItem>
                            ) : (
                              videoDevices.map((device) => (
                                <SelectItem
                                  key={device.deviceId}
                                  value={device.deviceId}
                                >
                                  {device.label ||
                                    `Camera ${videoDevices.indexOf(device) + 1}`}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <video
                        ref={valuationVideoRef}
                        autoPlay
                        playsInline
                        width={320}
                        height={240}
                        className='mb-2 aspect-video w-full max-w-xs rounded bg-black object-contain'
                        style={{ maxHeight: 320 }}
                      />
                      <Button
                        type='button'
                        onClick={handleValuationCapturePhoto}
                        className='mb-2'
                        disabled={
                          isEnumeratingDevices || videoDevices.length === 0
                        }
                      >
                        Capture
                      </Button>
                    </div>
                  )}
                  {albumValuationImagePreview && !showValuationCamera ? (
                    <img
                      src={albumValuationImagePreview}
                      alt='Album pressing preview'
                      className='mt-2 max-h-48 w-full rounded border object-contain shadow'
                      style={{ background: '#f3f3f3' }}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display =
                          'none';
                      }}
                    />
                  ) : (
                    !showValuationCamera && (
                      <span className='mt-2 text-sm text-muted-foreground'>
                        No preview available
                      </span>
                    )
                  )}
                </div>
                <div className='mt-4 flex flex-col space-y-1.5'>
                  <LocationLookup
                    onLocationFound={setAlbumValuationLocation}
                    defaultLocation='Worldwide'
                  />
                </div>
                {albumValuationError && (
                  <p className='text-sm text-destructive'>
                    {albumValuationError}
                  </p>
                )}
              </div>
            </CardContent>
            <CardFooter>
              <Button
                className='w-full'
                type='submit'
                disabled={albumValuationLoading}
              >
                {albumValuationLoading
                  ? 'Getting Valuation...'
                  : 'Get Valuation'}
              </Button>
            </CardFooter>
          </form>
        </Card>
        {albumValuationResult && (
          <Card className='mt-6 w-full'>
            <CardHeader>
              <CardTitle>Album Valuation Result</CardTitle>
            </CardHeader>
            <CardContent>
              {albumValuationResult.marketData?.recent?.listings?.[0]
                ?.condition && (
                <div className='mb-4'>
                  <span className='text-base font-semibold text-primary'>
                    Condition:{' '}
                    {
                      albumValuationResult.marketData.recent.listings[0]
                        .condition
                    }
                  </span>
                </div>
              )}
              <div className='mb-6'>
                <h3 className='mb-2 text-lg font-semibold'>Price Range</h3>
                <p className='text-2xl font-bold'>
                  {albumValuationResult.priceRange.currency}{' '}
                  {albumValuationResult.priceRange.min.toLocaleString()} -{' '}
                  {albumValuationResult.priceRange.max.toLocaleString()}
                </p>
              </div>
              <div className='mb-6'>
                <h3 className='mb-2 text-lg font-semibold'>Justification</h3>
                <p className='text-muted-foreground'>
                  {albumValuationResult.justification}
                </p>
              </div>
              {albumValuationResult.marketData.recent?.listings?.length > 0 && (
                <div className='mb-6'>
                  <h3 className='mb-2 text-lg font-semibold'>Recent Sales</h3>
                  <ul className='space-y-2'>
                    {albumValuationResult.marketData.recent.listings.map(
                      (sale, index) => (
                        <li key={index} className='border-b pb-2'>
                          <div className='flex items-start justify-between'>
                            <div>
                              <p className='font-medium'>
                                {sale.currency} {sale.price.toLocaleString()}
                              </p>
                              <p className='text-sm text-muted-foreground'>
                                {sale.date} • {sale.location}
                                {sale.condition && (
                                  <span>
                                    {' '}
                                    •{' '}
                                    <span className='font-semibold text-primary'>
                                      {sale.condition}
                                    </span>
                                  </span>
                                )}
                              </p>
                            </div>
                            {sale.url && (
                              <a
                                href={sale.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-sm text-blue-500 hover:underline'
                              >
                                View Sale
                              </a>
                            )}
                          </div>
                        </li>
                      ),
                    )}
                  </ul>
                  {albumValuationResult.marketData.recent.explanation && (
                    <p className='mt-2 text-sm text-muted-foreground'>
                      {albumValuationResult.marketData.recent.explanation}
                    </p>
                  )}
                </div>
              )}
              {albumValuationResult.marketData.current?.listings?.length >
                0 && (
                <div className='mb-6'>
                  <h3 className='mb-2 text-lg font-semibold'>
                    Current Listings
                  </h3>
                  <ul className='space-y-2'>
                    {albumValuationResult.marketData.current.listings.map(
                      (listing, index) => (
                        <li key={index} className='border-b pb-2'>
                          <div className='flex items-start justify-between'>
                            <div>
                              <p className='font-medium'>
                                {listing.currency}{' '}
                                {listing.price.toLocaleString()}
                              </p>
                              <p className='text-sm text-muted-foreground'>
                                {listing.location}
                                {listing.condition && (
                                  <span>
                                    {' '}
                                    •{' '}
                                    <span className='font-semibold text-primary'>
                                      {listing.condition}
                                    </span>
                                  </span>
                                )}
                              </p>
                            </div>
                            {listing.url && (
                              <a
                                href={listing.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-sm text-blue-500 hover:underline'
                              >
                                View Listing
                              </a>
                            )}
                          </div>
                        </li>
                      ),
                    )}
                  </ul>
                  {albumValuationResult.marketData.current.explanation && (
                    <p className='mt-2 text-sm text-muted-foreground'>
                      {albumValuationResult.marketData.current.explanation}
                    </p>
                  )}
                </div>
              )}
              {albumValuationResult.citations &&
                albumValuationResult.citations.length > 0 && (
                  <div className='mt-6'>
                    <h3 className='mb-2 text-lg font-semibold'>Citations</h3>
                    <ul className='list-disc space-y-1 pl-4'>
                      {albumValuationResult.citations.map((url, index) => (
                        <li key={index}>
                          <a
                            href={url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-500 hover:underline'
                          >
                            {url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              {albumValuationResult.searchResults &&
                albumValuationResult.searchResults.length > 0 && (
                  <div className='mt-6'>
                    <h3 className='mb-2 text-lg font-semibold'>
                      Search Results
                    </h3>
                    <ul className='space-y-2'>
                      {albumValuationResult.searchResults.map(
                        (result, index) => (
                          <li key={index} className='border-b pb-2'>
                            <a
                              href={result.url}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-blue-500 hover:underline'
                            >
                              {result.title}
                            </a>
                            {result.date && (
                              <span className='ml-2 text-sm text-muted-foreground'>
                                ({result.date})
                              </span>
                            )}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
